import React, { useState, useEffect } from "react";
import { Modal } from "../../../../shared/ui/modal";
import { ClinicalAlert, Criticality } from "../../domain/ClinicalAlert";
import { useCreateClinicalAlert, useUpdateClinicalAlert } from "../../application/useClinicalAlerts";
import { useTranslation } from "react-i18next";
import Button from "../../../../shared/ui/button/Button";
import Input from "../../../../shared/ui/form/input/InputField";
import Label from "../../../../shared/ui/form/Label";
import TextArea from "../../../../shared/ui/form/input/TextArea";
import Select from "../../../../shared/ui/form/Select";

interface ClinicalAlertModalProps {
    isOpen: boolean;
    onClose: () => void;
    alertToEdit?: ClinicalAlert | null;
}

const ClinicalAlertModal: React.FC<ClinicalAlertModalProps> = ({ isOpen, onClose, alertToEdit }) => {
    const { t } = useTranslation();
    const [name, setName] = useState("");
    const [context, setContext] = useState("");
    const [criticality, setCriticality] = useState<Criticality>(Criticality.LOW);

    const createMutation = useCreateClinicalAlert();
    const updateMutation = useUpdateClinicalAlert();

    const isPending = createMutation.isPending || updateMutation.isPending;

    useEffect(() => {
        if (isOpen) {
            if (alertToEdit) {
                setName(alertToEdit.name);
                setContext(alertToEdit.context);
                setCriticality(alertToEdit.criticality);
            } else {
                setName("");
                setContext("");
                setCriticality(Criticality.LOW);
            }
        }
    }, [alertToEdit, isOpen]);

    const handleSubmit = async () => {
        try {
            if (alertToEdit) {
                await updateMutation.mutateAsync({ id: alertToEdit.id, data: { name, context, criticality } });
            } else {
                await createMutation.mutateAsync({ name, context, criticality });
            }
            onClose();
        } catch (error) {
            console.error("Error saving alert", error);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} className="max-w-2xl">
            <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-6">
                    {alertToEdit ? t("clinicalAlerts.modal.titleEdit") : t("clinicalAlerts.modal.titleNew")}
                </h3>

                <div className="space-y-6">
                    <div>
                        <Label className="mb-2 block text-sm font-medium">
                            {t("clinicalAlerts.modal.nameLabel")}
                        </Label>
                        <Input
                            value={name}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
                            placeholder={t("clinicalAlerts.modal.namePlaceholder")}
                        />
                    </div>

                    <div>
                        <Label className="mb-2 block text-sm font-medium">
                            {t("clinicalAlerts.modal.contextLabel")}
                        </Label>
                        <TextArea
                            value={context}
                            onChange={(val: string) => setContext(val)}
                            rows={4}
                            placeholder={t("clinicalAlerts.modal.contextPlaceholder")}
                        />
                    </div>

                    <div>
                        <Label className="mb-2 block text-sm font-medium">
                            {t("clinicalAlerts.modal.criticalityLabel")}
                        </Label>
                        <Select
                            value={criticality}
                            onChange={(val: string) => setCriticality(val as Criticality)}
                            options={[
                                { value: Criticality.LOW, label: t("clinicalAlerts.modal.criticalityOptions.low") },
                                { value: Criticality.MEDIUM, label: t("clinicalAlerts.modal.criticalityOptions.medium") },
                                { value: Criticality.CRITICAL, label: t("clinicalAlerts.modal.criticalityOptions.critical") },
                            ]}
                        />
                    </div>

                    <div className="flex justify-end gap-3 pt-6 border-t border-gray-100 dark:border-gray-800">
                        <Button variant="outline" onClick={onClose}>
                            {t("clinicalAlerts.modal.cancel")}
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            disabled={!name || !context}
                            loading={isPending}
                        >
                            {alertToEdit ? t("clinicalAlerts.modal.save") : t("clinicalAlerts.modal.create")}
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default ClinicalAlertModal;
